import React, { useState, useEffect, useRef } from "react";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useDashboardUI } from "../../context/GlobalContext";
import feedbackService from "../../services/feedbackService";
import chatService from "../../services/chatService";
import { toast } from "react-toastify";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  RefreshCcw,
  Search,
  Filter,
  MoreVertical,
  TriangleAlert,
  ThumbsUp,
  ThumbsDown,
  Copy,
  ChevronDown,
  ChevronUp,
  CheckCheck,
  Siren,
  CheckCircle2,
  Clock,
  RotateCcw,
  MessageSquare,
  Eye,
  X,
  User,
  Mail,
  Building2,
  CalendarDays,
  Info,
} from "lucide-react";
import { DateTime } from "luxon";
import { displayUserName, formatUtcToLocal } from "../../helpers/helpers";

// ─── Jira-style Clickable Status Badge ───────────────────────────────────────
const StatusControl = ({ item, onMarkAddressed, onReopen, isSuperAdmin }) => {
  const [loading, setLoading] = useState(false);

  const StatusDropdown = ({ triggerEl, headerLabel, children }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{triggerEl}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        className="bg-white border border-gray-200 shadow-xl rounded-lg p-1 min-w-[180px]"
      >
        <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
          {headerLabel}
        </div>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (item.is_addressed) {
    if (!isSuperAdmin) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-violet-100 text-violet-600 text-sm font-medium whitespace-nowrap">
          Addressed
        </span>
      );
    }
    return (
      <StatusDropdown
        headerLabel="Transition to"
        triggerEl={
          <button
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-violet-100 text-violet-600 text-sm font-medium whitespace-nowrap hover:bg-violet-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-300 focus:ring-offset-1 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? <RefreshCcw className="h-3 w-3 animate-spin" /> : null}
            {loading ? "Saving…" : "Addressed"}
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </button>
        }
      >
        <DropdownMenuItem
          onClick={async () => { setLoading(true); await onReopen(item.id); setLoading(false); }}
          className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md cursor-pointer text-amber-700 hover:bg-amber-50 focus:bg-amber-50 outline-none transition-colors"
        >
          <RotateCcw className="h-4 w-4 shrink-0" />
          <span>Reopen</span>
          <span className="ml-auto">
            <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">PENDING</span>
          </span>
        </DropdownMenuItem>
      </StatusDropdown>
    );
  }

  return (
    <StatusDropdown
      headerLabel="Transition to"
      triggerEl={
        <button
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-gray-500 text-sm font-medium whitespace-nowrap hover:bg-gray-200 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1 disabled:opacity-50"
          disabled={loading}
        >
          {loading ? <RefreshCcw className="h-3 w-3 animate-spin" /> : null}
          {loading ? "Saving…" : "Pending"}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      }
    >
      <DropdownMenuItem
        onClick={async () => { setLoading(true); await onMarkAddressed(item.id); setLoading(false); }}
        className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md cursor-pointer text-green-700 hover:bg-green-50 focus:bg-green-50 outline-none transition-colors"
      >
        <CheckCheck className="h-4 w-4 shrink-0" />
        <span>Mark as addressed</span>
        <span className="ml-auto">
          <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">ADDRESSED</span>
        </span>
      </DropdownMenuItem>
    </StatusDropdown>
  );
};

// Selection Hint Badge with tooltip for bulk actions
const SelectionHint = ({ selectedFeedback, feedbackList, canCreateIncidents }) => {
  const negCount = feedbackList.filter(i => selectedFeedback.includes(i.id) && i.feedback_type === "thumbs_down").length;
  const pendingCount = feedbackList.filter(i => selectedFeedback.includes(i.id) && !i.is_addressed).length;

  const hints = [];
  if (negCount >= 2 && canCreateIncidents) hints.push(`${negCount} negative selected → Create Incident`);
  if (pendingCount >= 2) hints.push(`${pendingCount} pending selected → Mark Addressed`);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 bg-[#2B3674] text-white text-xs font-medium px-2.5 py-0.5 rounded-full cursor-default select-none">
            {selectedFeedback.length} selected
            <Info className="h-3 w-3 opacity-70" />
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl border-0 max-w-[240px]"
        >
          {hints.length > 0 ? (
            <div className="space-y-1">
              <p className="font-semibold text-white/60 uppercase tracking-wide text-[10px] mb-1.5">Bulk actions available</p>
              {hints.map((h, i) => (
                <p key={i} className="flex items-center gap-1.5">
                  <span className="h-1 w-1 rounded-full bg-white/50 shrink-0 mt-px" />
                  {h}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-white/70">Select 2+ negative or pending items to unlock bulk actions.</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FeedbackTable = () => {
  const {
    feedbackList, setFeedbackList,
    feedbackLoading, setFeedbackLoading,
    feedbackError, setFeedbackError,
    feedbackPagination, setFeedbackPagination,
    feedbackFilters, setFeedbackFilters,
    selectedFeedback, toggleFeedbackSelection,
    selectAllFeedback, clearFeedbackSelection, triggerRefresh,
    openCreateIncidentModal,
    isSuperAdmin, refreshTrigger, canCreateIncidents,
  } = useFeedback();

  const { selectedFacilityFilter, facilityList, facilityUserList } = useDashboardUI();

  const [categories, setCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [viewModal, setViewModal] = useState({ isOpen: false, item: null });
  const [linkedIncidents, setLinkedIncidents] = useState({ data: [], loading: false, fetchedFor: null });
  const [chatContext, setChatContext] = useState({ data: null, loading: false, fetchedFor: null, expanded: false });
  const categoryDropdownRef = useRef(null);
  const seenItemsRef = useRef(new Map());
  const [searchInput, setSearchInput] = useState(feedbackFilters.search || "");
  const [facilityMap, setFacilityMap] = useState(new Map());
  // Page-by-page pagination — the API only exposes a forward cursor, so we
  // keep the cursor that led to each page we've already visited and reuse it
  // to go back, instead of re-requesting from the start.
  const [page, setPage] = useState(1);
  const [cursorStack, setCursorStack] = useState([null]);
  const [dateRange] = useState(() => {
    const now = DateTime.now();
    return {
      startDate: now.minus({ days: 30 }).startOf("day").toISODate(),
      endDate: now.endOf("day").toISODate(),
    };
  });

  useEffect(() => {
    const handler = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounce typing (same 400ms as Prompt Management), then push into the filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setFeedbackFilters((prev) =>
        (prev.search || "") === searchInput.trim() ? prev : { ...prev, search: searchInput.trim() }
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const closeViewModal = () => {
    setViewModal({ isOpen: false, item: null });
    setLinkedIncidents({ data: [], loading: false, fetchedFor: null });
    setChatContext({ data: null, loading: false, fetchedFor: null, expanded: false });
  };

  const createFacilityMap = () => {
    const map = new Map();
    if (Array.isArray(facilityList)) {
      facilityList.forEach((f) => {
        if (f.facility_number && f.name) map.set(f.facility_number, f.name);
      });
    }
    setFacilityMap(map);
  };

  const getFacilityName = (id) => facilityMap.get(id) || `Facility ${id}`;
  const getUserName = (item) =>
    item.user_name || displayUserName(facilityUserList, item.user_id) || "Unknown";
  const getUserEmail = (item) => item.user_email || null;

  const fetchFeedbackList = async (cursor = null, reset = false) => {
    try {
      setFeedbackLoading(true);
      setFeedbackError(null);
      const filters = {
        ...feedbackFilters,
        cursor,
        facilityId: isSuperAdmin && selectedFacilityFilter?.facility_number
          ? selectedFacilityFilter.facility_number : null,
      };
      const result = await feedbackService.getFeedbackList(filters);
      if (reset || !cursor) setFeedbackList(result.items);
      else setFeedbackList((prev) => [...prev, ...result.items]);
      setFeedbackPagination({ total: result.total, has_more: result.has_more, next_cursor: result.next_cursor });
    } catch {
      setFeedbackError("Failed to load feedback list");
      toast.error("Failed to load feedback list");
    } finally {
      setFeedbackLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const result = await feedbackService.getFeedbackCategories();
      setCategories(result.categories || []);
    } catch {}
  };

  const fetchLinkedIncidents = async (feedbackId) => {
    if (linkedIncidents.fetchedFor === feedbackId) return;
    setLinkedIncidents({ data: [], loading: true, fetchedFor: feedbackId });
    try {
      const result = await feedbackService.getLinkedIncidents(feedbackId);
      setLinkedIncidents({ data: result.incidents || [], loading: false, fetchedFor: feedbackId });
    } catch {
      setLinkedIncidents({ data: [], loading: false, fetchedFor: feedbackId });
    }
  };

  const fetchChatContext = async (item) => {
    if (!item.chat_id || !item.message_id) return;
    if (chatContext.fetchedFor === item.id) return;
    setChatContext({ data: null, loading: true, fetchedFor: item.id, expanded: false });
    try {
      const result = await chatService.getConversationContext(item.chat_id, item.message_id, 4);
      setChatContext({ data: result, loading: false, fetchedFor: item.id, expanded: false });
    } catch {
      setChatContext({ data: null, loading: false, fetchedFor: item.id, expanded: false });
    }
  };

  const handleFilterChange = (key, value) => {
    const v = key === "isAddressed"
      ? (value === "all" ? null : value === "true")
      : (value === "all" ? "" : value);
    setFeedbackFilters((prev) => ({ ...prev, [key]: v }));
  };

  const toggleCategoryFilter = (catName) => {
    setFeedbackFilters((prev) => {
      const current = prev.categories || [];
      const isChecked = current.includes(catName);
      return {
        ...prev,
        categories: isChecked ? current.filter((c) => c !== catName) : [...current, catName],
      };
    });
  };

  const handleMarkSingleAsAddressed = async (id) => {
    try {
      await feedbackService.markFeedbackAsAddressed([id]);
      toast.success("Feedback marked as addressed");
      setPage(1); setCursorStack([null]);
      fetchFeedbackList(null, true);
    } catch { toast.error("Failed to mark feedback as addressed"); }
  };

  const handleReopen = async (id) => {
    try {
      await feedbackService.reopenFeedback([id]);
      toast.success("Feedback reopened for review");
      setPage(1); setCursorStack([null]);
      fetchFeedbackList(null, true);
    } catch { toast.error("Failed to reopen feedback"); }
  };

  const handleBulkMarkAsAddressed = async () => {
    // Every pending selected row, across all pages visited — same set the
    // button's count shows.
    const pendingIds = selectedItems.filter((item) => !item.is_addressed).map((item) => item.id);
    if (!pendingIds.length) return;
    try {
      await feedbackService.markFeedbackAsAddressed(pendingIds);
      toast.success(`Marked ${pendingIds.length} items as addressed`);
      clearFeedbackSelection();
      setPage(1); setCursorStack([null]);
      fetchFeedbackList(null, true);
    } catch { toast.error("Failed to mark feedback as addressed"); }
  };

  const clearFilters = () => {
    setFeedbackFilters((prev) => ({ feedbackType: "", category: "", categories: [], isAddressed: null, startDate: null, endDate: null, facilityId: null, limit: 20, search: prev.search }));
  };

  const formatCategory = (cat) =>
    cat ? cat.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : null;

  // Search is handled entirely by the backend (the `search` filter is sent with
  // the list request); rows are not filtered by search on the client.
  const selectedCategories = feedbackFilters.categories || [];
  const displayedFeedback = feedbackList.filter(
    (item) => selectedCategories.length === 0 || selectedCategories.includes(item.category)
  );
  // Remember every row seen so a selection can span pages: the list only holds
  // the current page, but selected ids from earlier pages still resolve here.
  feedbackList.forEach((item) => seenItemsRef.current.set(item.id, item));
  const selectedItems = selectedFeedback.map((id) => seenItemsRef.current.get(id)).filter(Boolean);
  const hasActiveFilters = !!feedbackFilters.feedbackType || selectedCategories.length > 0 || feedbackFilters.isAddressed !== null;

  const getActions = (item) => {
    const actions = [];
    actions.push({
      id: "view", label: "View details", icon: Eye,
      cls: "text-gray-700 hover:bg-gray-100 focus:bg-gray-100",
      onClick: () => { setViewModal({ isOpen: true, item }); fetchLinkedIncidents(item.id); fetchChatContext(item); },
    });
    if (canCreateIncidents && item.feedback_type === "thumbs_down") {
      // If this item is part of a multi-selection, pass all selected negative IDs
      const isInSelection = selectedFeedback.includes(item.id) && selectedFeedback.length > 1;
      const feedbackIds = isInSelection
        ? selectedItems.filter(f => f.feedback_type === "thumbs_down").map(f => f.id)
        : [item.id];
      const label = feedbackIds.length > 1 ? `Create incident (${feedbackIds.length})` : "Create incident";
      actions.push({
        id: "incident", label, icon: Siren,
        cls: "text-red-600 hover:bg-red-50 focus:bg-red-50", sep: true,
        onClick: () => openCreateIncidentModal(feedbackIds),
      });
    }
    return actions;
  };

  useEffect(() => {
    setPage(1);
    setCursorStack([null]);
    clearFeedbackSelection();
    fetchFeedbackList(null, true);
  }, [feedbackFilters, selectedFacilityFilter, refreshTrigger, dateRange]);

  const goToNextPage = () => {
    if (!feedbackPagination.has_more || !feedbackPagination.next_cursor || feedbackLoading) return;
    const nextCursor = feedbackPagination.next_cursor;
    fetchFeedbackList(nextCursor, true);
    setCursorStack((prev) => [...prev, nextCursor]);
    setPage((p) => p + 1);
  };

  const goToPrevPage = () => {
    if (page <= 1 || feedbackLoading) return;
    const prevCursor = cursorStack[page - 2] ?? null;
    fetchFeedbackList(prevCursor, true);
    setCursorStack((prev) => prev.slice(0, page - 1));
    setPage((p) => p - 1);
  };

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { createFacilityMap(); }, [facilityList]);

  const categoryTriggerLabel = selectedCategories.length === 0
    ? "All categories"
    : selectedCategories.length === 1
      ? categories.find(c => c.name === selectedCategories[0])?.display_name || selectedCategories[0]
      : `${selectedCategories.length} selected`;

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0">
        <div className="flex items-center gap-4 flex-1 min-w-0 w-full sm:w-auto">
          <div className="relative flex-1 min-w-0">
            <Search className="pointer-events-none absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user, category or comment…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full h-9 sm:h-11 rounded-xl border border-gray-100 bg-white shadow-sm pl-9 sm:pl-11 pr-3 sm:pr-5 text-xs sm:text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-200"
            />
          </div>
          {/* Phones: refresh icon sits beside the search bar (the top Refresh button is hidden on this tab) */}
          <button
            type="button"
            onClick={() => {
              clearFeedbackSelection();
              triggerRefresh();
            }}
            disabled={feedbackLoading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-100 bg-white text-slate-700 shadow-sm hover:border-gray-300 transition-colors disabled:opacity-60 sm:hidden"
          >
            <RefreshCcw className={`h-4 w-4 ${feedbackLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {selectedFeedback.length >= 2 && (
            <SelectionHint
              selectedFeedback={selectedFeedback}
              feedbackList={selectedItems}
              canCreateIncidents={canCreateIncidents}
            />
          )}
          {(() => {
            const pendingSelected = selectedItems.filter(item => !item.is_addressed);
            const negativeSelected = selectedItems.filter(item => item.feedback_type === "thumbs_down");
            return (
              <>
                {selectedFeedback.length >= 2 && negativeSelected.length >= 2 && canCreateIncidents && (
                  <Button
                    onClick={() => openCreateIncidentModal(negativeSelected.map(i => i.id))}
                    size="sm"
                    className="bg-[#2B3674] hover:bg-[#1E2951] text-white gap-1.5"
                  >
                    <Siren className="h-4 w-4" />
                    Create Incident ({negativeSelected.length})
                  </Button>
                )}
                {pendingSelected.length >= 2 && (
                  <Button onClick={handleBulkMarkAsAddressed} size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1.5">
                    <CheckCheck className="h-4 w-4" />
                    Mark Addressed ({pendingSelected.length})
                  </Button>
                )}
              </>
            );
          })()}
          {/* Phones: the type filter and Filters button share one line */}
          <div className="flex w-full flex-nowrap items-center gap-2 sm:contents">
          {/* Type quick filter — segmented control (same state/handler as before) */}
          <div className="inline-flex min-w-0 flex-1 items-center gap-0.5 rounded-xl border border-gray-200 bg-white p-1 sm:flex-none">
            {[
              { value: "all", label: "All" },
              { value: "thumbs_up", label: "Positive" },
              { value: "thumbs_down", label: "Negative" },
            ].map((o) => {
              const active = (feedbackFilters.feedbackType || "all") === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => handleFilterChange("feedbackType", o.value)}
                  className={`h-7 flex-1 px-2.5 sm:flex-none sm:px-3.5 rounded-lg text-sm transition-colors cursor-pointer ${
                    active
                      ? "bg-[#14122B] text-white font-semibold"
                      : "text-gray-500 font-medium hover:text-gray-900"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            size="sm"
            variant={showFilters ? "default" : "outline"}
            className={`shrink-0 gap-1.5 transition-all duration-150 ${
              showFilters
                ? "bg-[#2B3674] text-white border-[#2B3674] hover:bg-[#1a2456]"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-300"
            }`}
          >
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className={`h-2 w-2 rounded-full ${showFilters ? "bg-white/80" : "bg-[#2B3674]"}`} />
            )}
          </Button>
          </div>
        </div>
      </div>

      {/* ── Filters Panel ──────────────────────────────────────────────────── */}
      {showFilters && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Category — custom multi-select */}
            <div className="relative" ref={categoryDropdownRef}>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Category</label>
              <button
                type="button"
                onClick={() => setCategoryDropdownOpen((v) => !v)}
                className={`w-full flex items-center justify-between px-3 h-9 text-sm border rounded-md bg-white transition-colors ${
                  categoryDropdownOpen
                    ? "border-[#2B3674] ring-1 ring-[#2B3674]/20"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <span className={`truncate ${selectedCategories.length === 0 ? "text-gray-500" : "text-gray-800 font-medium"}`}>
                  {categoryTriggerLabel}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-400 shrink-0 ml-2 transition-transform duration-150 ${categoryDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {categoryDropdownOpen && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                  <div
                    className="flex items-center gap-2.5 px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setFeedbackFilters((prev) => ({ ...prev, categories: [] }))}
                  >
                    <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      selectedCategories.length === 0 ? "bg-[#2B3674] border-[#2B3674]" : "border-gray-300 bg-white"
                    }`}>
                      {selectedCategories.length === 0 && (
                        <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 12 12">
                          <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-700">All categories</span>
                  </div>
                  <div className="max-h-52 overflow-y-auto py-1">
                    {categories.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-gray-400 text-center">No categories available</div>
                    ) : (
                      categories.map((cat) => {
                        const isChecked = selectedCategories.includes(cat.name);
                        return (
                          <div
                            key={cat.name}
                            className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isChecked ? "bg-blue-50/60 hover:bg-blue-50" : "hover:bg-gray-50"}`}
                            onClick={() => toggleCategoryFilter(cat.name)}
                          >
                            <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                              isChecked ? "bg-[#2B3674] border-[#2B3674]" : "border-gray-300 bg-white"
                            }`}>
                              {isChecked && (
                                <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 12 12">
                                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </div>
                            <span className={`text-sm ${isChecked ? "text-gray-900 font-medium" : "text-gray-700"}`}>
                              {cat.display_name}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {selectedCategories.length > 0 && (
                    <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                      <span className="text-xs text-gray-500">{selectedCategories.length} selected</span>
                      <button
                        onClick={() => setFeedbackFilters((prev) => ({ ...prev, categories: [] }))}
                        className="text-xs text-[#2B3674] hover:underline font-medium cursor-pointer"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
              <Select
                value={feedbackFilters.isAddressed === null ? "all" : feedbackFilters.isAddressed === true ? "true" : "false"}
                onValueChange={(v) => handleFilterChange("isAddressed", v)}
              >
                <SelectTrigger className="bg-white border-gray-200 text-sm h-9 hover:border-gray-300 hover:bg-gray-50 transition-colors focus:ring-1 focus:ring-[#2B3674]/20 focus:border-[#2B3674]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg p-1">
                  {[
                    { value: "all", label: "All statuses" },
                    { value: "false", label: "Pending" },
                    { value: "true", label: "Addressed" },
                  ].map((o) => (
                    <SelectItem key={o.value} value={o.value} className="text-sm rounded-md cursor-pointer hover:bg-gray-100 focus:bg-gray-100 outline-none transition-colors">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

          </div>
          {hasActiveFilters && (
            <div className="flex justify-end mt-3">
              <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-2 transition-colors cursor-pointer">
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Table + Pagination — the table area is flex-1 (scrolls
           internally, like Prompt Management's table), and the pagination
           bar sits right after it, pinned in place — it never moves with
           the inner table scroll. ─────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 min-h-0 bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
        <div className="table-scroll" style={{ height: "100%", overflowY: "auto", overflowX: "auto" }}>
          <Table className="min-w-[1046px] border-collapse [&>div]:overflow-visible">
            <TableHeader>
              <TableRow className="border-b border-gray-100 hover:bg-indigo-50">
                <TableHead className="px-0" style={{ position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20, verticalAlign: "middle", textAlign: "center", width: "48px", minWidth: "48px" }}>
                  <input
                    type="checkbox"
                    checked={displayedFeedback.length > 0 && displayedFeedback.every((i) => selectedFeedback.includes(i.id))}
                    onChange={(e) => {
                      const pageIds = displayedFeedback.map((i) => i.id);
                      selectAllFeedback(
                        e.target.checked
                          ? [...new Set([...selectedFeedback, ...pageIds])]
                          : selectedFeedback.filter((id) => !pageIds.includes(id))
                      );
                    }}
                    className="rounded border-gray-300 text-[#2B3674] focus:ring-[#2B3674] cursor-pointer align-middle"
                  />
                </TableHead>
                {[
                  { label: "Type",     width: "120px", align: "left" },
                  { label: "User",     width: "220px", align: "left" },
                  { label: "Facility", width: "130px", align: "left" },
                  { label: "Category", width: "210px", align: "left" },
                  { label: "Status",   width: "150px", align: "left" },
                  { label: "Date",     width: "120px", align: "left" },
                  { label: "",         width: "48px",  align: "center" },
                ].map(({ label, width, align }, i, arr) => (
                  <TableHead
                    key={label || "actions"}
                    style={{
                      position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20,
                      verticalAlign: "middle", width, minWidth: width, textAlign: align,
                    }}
                    className={`text-xs font-semibold text-gray-500 uppercase tracking-wide py-3.5 ${align === "left" ? "pl-3" : ""}`}
                  >
                    {label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {feedbackLoading && feedbackList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <RefreshCcw className="h-5 w-5 animate-spin" />
                      <span className="text-sm">Loading feedback…</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : feedbackError ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-red-500">
                      <TriangleAlert className="h-6 w-6" />
                      <span className="text-sm">{feedbackError}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : displayedFeedback.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <Filter className="h-5 w-5" />
                      <span className="text-sm">No feedback items found</span>
                      {hasActiveFilters && (
                        <button onClick={clearFilters} className="text-xs text-[#2B3674] hover:underline cursor-pointer">Clear filters</button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                displayedFeedback.map((item) => {
                  const actions = getActions(item);
                  const isSelected = selectedFeedback.includes(item.id);
                  return (
                    <TableRow
                      key={item.id}
                      className={`transition-colors border-b border-gray-100 last:border-0 ${isSelected ? "bg-blue-50/50" : "hover:bg-gray-50"}`}
                    >
                      <TableCell className="px-0" style={{ verticalAlign: "middle", textAlign: "center", width: "48px", minWidth: "48px" }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleFeedbackSelection(item.id)}
                          className="rounded border-gray-300 text-[#2B3674] focus:ring-[#2B3674] cursor-pointer align-middle"
                        />
                      </TableCell>
                      <TableCell style={{ verticalAlign: "middle", textAlign: "left", width: "120px" }} className="pl-3 py-3">
                        {item.feedback_type === "thumbs_up" ? (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600">
                            Positive
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-red-50 px-3 py-1 text-sm font-medium text-red-600">
                            Negative
                          </span>
                        )}
                      </TableCell>
                      <TableCell style={{ verticalAlign: "middle", textAlign: "left", width: "220px" }} className="pl-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 shrink-0">
                            {getUserName(item)
                              .split(" ")
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <span className="text-sm font-semibold text-gray-900 block truncate">{getUserName(item)}</span>
                            {getUserEmail(item) && (
                              <span className="text-xs text-gray-400 block truncate">{getUserEmail(item)}</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell style={{ verticalAlign: "middle", textAlign: "left", width: "130px" }} className="pl-3 py-3">
                        <span className="text-sm text-gray-600">{getFacilityName(item.facility_id)}</span>
                      </TableCell>
                      <TableCell style={{ verticalAlign: "middle", width: "210px" }} className="pl-3 py-3">
                        <div className="flex items-center justify-start gap-2">
                          {formatCategory(item.category)
                            ? (
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
                                {formatCategory(item.category)}
                              </span>
                            )
                            : <span className="text-sm text-gray-400">—</span>
                          }
                          {item.comment && (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="shrink-0 cursor-default">
                                    <MessageSquare className="h-3.5 w-3.5 text-[#2B3674] opacity-70 hover:opacity-100 transition-opacity" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  align="start"
                                  className="max-w-xs bg-gray-900 text-white text-xs rounded-lg px-3 py-2 leading-relaxed shadow-xl border-0 whitespace-pre-wrap break-words"
                                >
                                  {item.comment}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell style={{ verticalAlign: "middle", textAlign: "left", width: "150px" }} className="pl-3 py-3">
                        <div className="flex justify-start">
                          <StatusControl item={item} onMarkAddressed={handleMarkSingleAsAddressed} onReopen={handleReopen} isSuperAdmin={isSuperAdmin} />
                        </div>
                      </TableCell>
                      <TableCell style={{ verticalAlign: "middle", textAlign: "left", width: "120px" }} className="pl-3 py-3">
                        <span className="text-sm text-gray-600 whitespace-nowrap">
                          {formatUtcToLocal(item.created_at, "MMM DD, YYYY")}
                        </span>
                      </TableCell>
                      <TableCell style={{ verticalAlign: "middle", textAlign: "center", width: "48px" }}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors cursor-pointer">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-xl rounded-lg min-w-[180px] p-1">
                            {actions.map((action, idx) => {
                              const Icon = action.icon;
                              return (
                                <React.Fragment key={action.id}>
                                  {action.sep && idx > 0 && <DropdownMenuSeparator className="bg-gray-100 my-1" />}
                                  <DropdownMenuItem
                                    onClick={action.onClick}
                                    className={`flex items-center gap-2.5 px-3 py-2 text-sm rounded-md cursor-pointer select-none outline-none transition-colors ${action.cls}`}
                                  >
                                    <Icon className="h-4 w-4 shrink-0" />
                                    {action.label}
                                  </DropdownMenuItem>
                                </React.Fragment>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ── Pagination (page-by-page, same layout as Prompt Management) ────── */}
      <div className="mt-auto pt-3 pb-1 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm shrink-0">
        <span>Total {feedbackPagination.total}</span>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            Rows per page
            <select
              value={feedbackFilters.limit || 20}
              onChange={(e) => setFeedbackFilters((prev) => ({ ...prev, limit: Number(e.target.value) }))}
              className="rounded-md border px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button disabled={page <= 1 || feedbackLoading} onClick={goToPrevPage} className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl">‹</button>

            <span>
              {feedbackPagination.total === 0
                ? "0 / 0"
                : `${page} / ${Math.max(1, Math.ceil(feedbackPagination.total / (feedbackFilters.limit || 20)))}`}
            </span>

            <button
              disabled={!feedbackPagination.has_more || feedbackLoading}
              onClick={goToNextPage}
              className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl"
            >
              ›
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* ── View Details Modal ─────────────────────────────────────────────── */}
      {viewModal.isOpen && viewModal.item && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeViewModal(); }}
        >
          {/* Modal card — flex column so header/footer are pinned and body scrolls */}
          <Card className="w-full max-w-lg shadow-2xl bg-white rounded-2xl overflow-hidden flex flex-col max-h-[90vh]">

            {/* ── Pinned header ── */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 shrink-0">
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-[#2B3674]" />
                <h2 className="text-lg font-semibold text-gray-900">Feedback Details</h2>
              </div>
              <Button
                variant="ghost" size="sm"
                onClick={closeViewModal}
                className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* ── Scrollable body ── */}
            <div className="px-6 pb-4 overflow-y-auto flex-1 space-y-4">

              {/* Submitted by */}
              <div className="bg-gray-50 rounded-lg border border-gray-100 p-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Submitted by</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2.5">
                    <User className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-sm font-medium text-gray-900">{getUserName(viewModal.item)}</span>
                  </div>
                  {getUserEmail(viewModal.item) && (
                    <div className="flex items-center gap-2.5">
                      <Mail className="h-4 w-4 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-600">{getUserEmail(viewModal.item)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2.5">
                    <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-600">{getFacilityName(viewModal.item.facility_id)}</span>
                  </div>
                </div>
              </div>

              {/* Type / Category / Status / Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Type</p>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5">
                    {viewModal.item.feedback_type === "thumbs_up" ? (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-0 gap-1 text-xs font-medium">
                        <ThumbsUp className="h-3 w-3" />Positive
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border-0 gap-1 text-xs font-medium">
                        <ThumbsDown className="h-3 w-3" />Negative
                      </Badge>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Category</p>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5">
                    <span className="text-sm text-gray-700">
                      {formatCategory(viewModal.item.category) || <span className="text-gray-400">—</span>}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Status</p>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5">
                    {viewModal.item.is_addressed ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
                        <CheckCircle2 className="h-3.5 w-3.5" />Addressed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700">
                        <Clock className="h-3.5 w-3.5" />Pending
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Date</p>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-600">{formatUtcToLocal(viewModal.item.created_at, "MMM DD, YYYY")}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comment */}
              {viewModal.item.comment && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Comment</p>
                  <div className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {viewModal.item.comment}
                  </div>
                </div>
              )}

              {/* Conversation Context */}
              {(chatContext.loading || chatContext.data) && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Conversation Context</p>
                    {chatContext.data?.messages?.length > 0 && (
                      <button
                        onClick={() => setChatContext((prev) => ({ ...prev, expanded: !prev.expanded }))}
                        className="flex items-center gap-1 text-xs text-[#2B3674] hover:underline cursor-pointer"
                      >
                        {chatContext.expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {chatContext.expanded ? "Hide" : "Show"} ({chatContext.data.messages.length} messages)
                      </button>
                    )}
                  </div>
                  {chatContext.loading ? (
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5 text-xs text-gray-400">
                      <RefreshCcw className="h-3.5 w-3.5 animate-spin shrink-0" />
                      Loading conversation context…
                    </div>
                  ) : chatContext.data?.messages?.length > 0 ? (
                    <>
                      <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-100 px-3 py-2 text-xs text-gray-500">
                        <MessageSquare className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                        <span>
                          {chatContext.data.messages.length} messages around the flagged response
                          {viewModal.item?.chat_id && (
                            <span className="ml-2 font-mono text-gray-300">{String(viewModal.item.chat_id).slice(0, 8)}…</span>
                          )}
                        </span>
                      </div>
                      {chatContext.expanded && (
                        <div className="mt-2 bg-gray-50 rounded-lg border border-gray-100 p-2 space-y-1.5">
                          {chatContext.data.messages.map((msg, i) => {
                            const isTarget = chatContext.data.target_message_id === msg.id;
                            return (
                              <div
                                key={msg.id || i}
                                className={`text-xs px-2.5 py-2 rounded-lg whitespace-pre-wrap leading-relaxed ${
                                  msg.role === "user"
                                    ? "bg-blue-50 text-blue-900 border border-blue-100"
                                    : "bg-green-50 text-green-900 border border-green-100"
                                } ${isTarget ? "ring-2 ring-amber-300" : ""}`}
                              >
                                <p className="font-semibold mb-0.5 opacity-60">
                                  {msg.role === "assistant" ? "Assistant" : "User"}
                                </p>
                                {msg.content || msg.text || ""}
                                {/* Attachment indicators */}
                                {(msg.has_generated_image || msg.image_paths?.length > 0 || msg.generated_files || msg.related_files) && (
                                  <div className="flex flex-wrap gap-1 mt-1.5 pt-1.5 border-t border-black/5">
                                    {(msg.has_generated_image || msg.image_paths?.length > 0) && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-black/5 text-current opacity-70">
                                        🖼 {msg.image_paths?.length > 1 ? `${msg.image_paths.length} images` : "Image"}
                                      </span>
                                    )}
                                    {msg.generated_files && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-black/5 text-current opacity-70">
                                        📄 Generated file
                                      </span>
                                    )}
                                    {msg.related_files && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-black/5 text-current opacity-70">
                                        📎 Attached file
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5 text-xs text-gray-400">
                      No conversation context available.
                    </div>
                  )}
                </div>
              )}

              {/* Linked Incidents */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Linked Incidents</p>
                {linkedIncidents.loading ? (
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5 text-xs text-gray-400">
                    <RefreshCcw className="h-3.5 w-3.5 animate-spin shrink-0" />
                    Checking for linked incidents…
                  </div>
                ) : linkedIncidents.data.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5 text-xs text-gray-400">
                    No incidents have been created from this feedback.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {linkedIncidents.data.map((incident) => {
                      const statusCls = {
                        reported:             "bg-gray-100 text-gray-600 border-gray-200",
                        assigned_to_facility: "bg-blue-50 text-blue-700 border-blue-200",
                        assigned_to_expert:   "bg-violet-50 text-violet-700 border-violet-200",
                        in_progress:          "bg-amber-50 text-amber-700 border-amber-200",
                        resolved:             "bg-green-100 text-green-700 border-green-200",
                        closed:               "bg-gray-100 text-gray-500 border-gray-200",
                      }[incident.status] || "bg-gray-100 text-gray-600 border-gray-200";

                      const statusLabel = {
                        reported:             "Reported",
                        assigned_to_facility: "Assigned to Facility",
                        assigned_to_expert:   "Assigned to Expert",
                        in_progress:          "In Progress",
                        resolved:             "Resolved",
                        closed:               "Closed",
                      }[incident.status] || incident.status;

                      const priorityCls = {
                        low:      "bg-green-100 text-green-700 border-green-200",
                        medium:   "bg-amber-50 text-amber-700 border-amber-200",
                        high:     "bg-orange-100 text-orange-700 border-orange-200",
                        critical: "bg-red-100 text-red-700 border-red-200",
                      }[incident.priority] || "bg-gray-100 text-gray-600 border-gray-200";

                      return (
                        <div key={incident.id} className="flex items-center justify-between gap-3 bg-gray-50 rounded-lg border border-gray-100 px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Siren className="h-3.5 w-3.5 text-[#2B3674] shrink-0" />
                            <div className="min-w-0">
                              <span className="text-sm font-medium text-gray-800 truncate block">{incident.title}</span>
                              <span className="text-xs text-gray-400">
                                {incident.created_by_name} · {formatUtcToLocal(incident.created_at, "MMM DD, YYYY")}
                                {incident.resolved_at && <span className="text-green-600 ml-1">· Resolved</span>}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium border ${priorityCls}`}>
                              {incident.priority.charAt(0).toUpperCase() + incident.priority.slice(1)}
                            </span>
                            <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium border ${statusCls}`}>
                              {statusLabel}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>{/* end scrollable body */}

            {/* ── Pinned footer ── */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-white shrink-0">
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(String(viewModal.item.id));
                  toast.success("Feedback ID copied");
                }}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 transition-colors cursor-pointer group"
                title="Copy feedback ID"
              >
                <Copy className="h-3.5 w-3.5 group-hover:text-[#2B3674] transition-colors" />
                <span className="font-mono">{String(viewModal.item.id).slice(0, 8)}…</span>
              </button>
              <Button
                variant="outline"
                onClick={closeViewModal}
                className="text-gray-600 hover:bg-gray-100"
              >
                Close
              </Button>
            </div>

          </Card>
        </div>
      )}

    </div>
  );
};

export default FeedbackTable;